import React, { useCallback, useEffect, useState } from 'react';
import { ApiUsage, DailyUsage, ServiceUsage, getApiUsage } from '../api.apiUsage';
import SectionLoader from '../components/SectionLoader';

const getToken = () => localStorage.getItem('token') || '';

const WINDOWS = [7, 30, 90, 365];

/** Above this share of failed calls, quota is being spent on nothing and something is wrong. */
const WASTE_WARNING = 0.2;

const SERVICE_LABELS: Record<string, string> = {
  ttlock: 'TTLock (smart locks)',
  'ttlock-sidecar': 'TTLock sidecar (internal)',
  hostex: 'Hostex (channel manager)',
  stripe: 'Stripe',
  ubyport: 'Ubyport',
  weather: 'Weather',
  email: 'Email (SES)',
};

const label = (service: string) => SERVICE_LABELS[service] ?? service;

const number = (value: number) => value.toLocaleString();

const AdminApiUsage: React.FC = () => {
  const [usage, setUsage] = useState<ApiUsage | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchUsage = useCallback(async (window: number) => {
    setLoading(true);
    setError(null);
    try {
      setUsage(await getApiUsage(getToken(), window));
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load API usage');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsage(days); }, [fetchUsage, days]);

  const services = usage?.services ?? [];

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: 2, color: '#1a202c' }}>API Usage</h1>
          <p style={{ color: '#718096', marginBottom: 0, fontSize: '0.9rem' }}>
            Every call we make to a third-party API, counted where it is billed.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {WINDOWS.map(window => (
            <button
              key={window}
              onClick={() => setDays(window)}
              style={{
                padding: '7px 14px', borderRadius: 6, fontWeight: 600, fontSize: '0.82rem',
                cursor: 'pointer',
                border: `1px solid ${days === window ? '#2563eb' : '#e2e8f0'}`,
                background: days === window ? '#2563eb' : '#fff',
                color: days === window ? '#fff' : '#4a5568',
              }}
            >
              {window === 365 ? '1 year' : `${window} days`}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ padding: 16, color: '#c53030', background: '#fff5f5', borderRadius: 8, marginBottom: 16 }}>{error}</div>
      )}

      {loading && !usage ? (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <SectionLoader label="Loading API usage…" minHeight={200} />
        </div>
      ) : services.length === 0 ? (
        !error && (
          <div style={{ color: '#718096', padding: 32, textAlign: 'center', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            Nothing counted in this window yet. Counting starts when the backend is deployed —
            history before that was never recorded.
          </div>
        )
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {services.map(service => (
            <ServiceCard
              key={service.service}
              service={service}
              days={days}
              open={expanded === service.service}
              onToggle={() => setExpanded(expanded === service.service ? null : service.service)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ServiceCard: React.FC<{
  service: ServiceUsage;
  days: number;
  open: boolean;
  onToggle: () => void;
}> = ({ service, days, open, onToggle }) => {
  const wasteShare = service.totalCalls > 0 ? service.totalErrors / service.totalCalls : 0;
  const wasteful = wasteShare >= WASTE_WARNING;

  // Straight-line projection from the window actually measured. Crude on purpose: it answers
  // "will this hit the ceiling", not "exactly when".
  const perDay = service.totalCalls / Math.max(1, days);
  const projectedYear = Math.round(perDay * 365);
  const quotaShare = service.quota ? service.totalCalls / service.quota : null;

  return (
    <div style={{ background: '#fff', borderRadius: 10, border: `1px solid ${wasteful ? '#fbd5d5' : '#e2e8f0'}`, overflow: 'hidden' }}>
      <div style={{ padding: '16px 18px', display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c' }}>{label(service.service)}</div>
          <div style={{ color: '#a0aec0', fontSize: '0.75rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', marginTop: 2 }}>
            {service.service}
          </div>
        </div>

        <Metric label={`Calls · ${days}d`} value={number(service.totalCalls)} />
        <Metric label="Last 24h" value={number(service.callsLast24h)} />
        <Metric
          label="Failed"
          value={`${number(service.totalErrors)}${service.totalCalls > 0 ? ` (${Math.round(wasteShare * 100)}%)` : ''}`}
          color={wasteful ? '#c53030' : undefined}
        />
        <Metric label="At this rate / year" value={number(projectedYear)} />

        <button
          onClick={onToggle}
          style={{
            padding: '7px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff',
            color: '#2563eb', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', alignSelf: 'center',
          }}
        >
          {open ? 'Hide detail' : 'Detail'}
        </button>
      </div>

      {service.quota != null && (
        <div style={{ padding: '0 18px 14px' }}>
          <QuotaBar used={service.totalCalls} quota={service.quota} period={service.quotaPeriod} days={days} />
        </div>
      )}

      {wasteful && (
        <div style={{ padding: '10px 18px', background: '#fff5f5', color: '#9b2c2c', fontSize: '0.82rem', borderTop: '1px solid #fed7d7' }}>
          {Math.round(wasteShare * 100)}% of these calls were refused by the provider. Quota is charged
          for a refused call, so this is spend buying nothing — check the breakdown for the operation
          responsible.
        </div>
      )}

      {open && (
        <div style={{ borderTop: '1px solid #edf2f7', padding: '16px 18px', background: '#fbfcfe' }}>
          <DailyChart daily={service.daily} />

          <div style={{ overflowX: 'auto', marginTop: 18 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <Th>Endpoint</Th>
                  <Th>Triggered by</Th>
                  <Th align="right">Calls</Th>
                  <Th align="right">Failed</Th>
                </tr>
              </thead>
              <tbody>
                {service.operations.map(op => (
                  <tr key={`${op.operation}-${op.trigger}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '9px 12px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.78rem', color: '#1a202c' }}>
                      {op.operation}
                      {op.lastError && (
                        <div style={{ color: '#c53030', fontSize: '0.74rem', marginTop: 3, fontFamily: 'inherit', maxWidth: 420, whiteSpace: 'normal' }}>
                          {op.lastError}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#4a5568', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.78rem' }}>
                      {op.trigger || '—'}
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: '#1a202c', fontVariantNumeric: 'tabular-nums' }}>{number(op.calls)}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: op.errors > 0 ? '#c53030' : '#a0aec0', fontVariantNumeric: 'tabular-nums' }}>
                      {number(op.errors)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const QuotaBar: React.FC<{ used: number; quota: number; period: string | null; days: number }> = ({ used, quota, period, days }) => {
  // The window rarely matches the quota period, so show what is measured against the ceiling and
  // say plainly which window it covers — a "63% of your annual quota" claim from 7 days of data
  // would be a lie.
  const share = Math.min(1, used / quota);
  const over = used > quota;
  const colour = over ? '#e53e3e' : share > 0.7 ? '#d97706' : '#38a169';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.76rem', color: '#718096', marginBottom: 5 }}>
        <span>{number(used)} calls in the last {days} days</span>
        <span>plan allows {number(quota)} per {period ?? 'period'}</span>
      </div>
      <div style={{ height: 7, background: '#edf2f7', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${share * 100}%`, height: '100%', background: colour, borderRadius: 4 }} />
      </div>
    </div>
  );
};

const DailyChart: React.FC<{ daily: DailyUsage[] }> = ({ daily }) => {
  if (daily.length === 0) return null;
  const peak = Math.max(...daily.map(d => d.calls), 1);

  return (
    <div>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Calls per day · peak {number(peak)}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 90, overflowX: 'auto', paddingBottom: 2 }}>
        {daily.map(day => {
          const failedHeight = (day.errors / peak) * 100;
          const okHeight = ((day.calls - day.errors) / peak) * 100;
          return (
            <div
              key={day.day}
              title={`${day.day} — ${number(day.calls)} calls, ${number(day.errors)} failed`}
              style={{ flex: '1 0 6px', minWidth: 4, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
            >
              {/* Failures stacked on top, so a bar that is mostly red reads as wasted at a glance. */}
              <div style={{ height: `${failedHeight}%`, background: '#e53e3e', borderRadius: '2px 2px 0 0' }} />
              <div style={{ height: `${okHeight}%`, background: '#90cdf4', borderRadius: failedHeight > 0 ? 0 : '2px 2px 0 0' }} />
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#a0aec0', marginTop: 4 }}>
        <span>{daily[0].day}</span>
        <span>{daily[daily.length - 1].day}</span>
      </div>
    </div>
  );
};

const Metric: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <div style={{ minWidth: 96 }}>
    <div style={{ fontSize: '1.15rem', fontWeight: 700, color: color ?? '#1a202c', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    <div style={{ fontSize: '0.72rem', color: '#718096', marginTop: 1 }}>{label}</div>
  </div>
);

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <th style={{
    textAlign: align, padding: '9px 12px', fontSize: '0.7rem', fontWeight: 700,
    color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em',
  }}>
    {children}
  </th>
);

export default AdminApiUsage;
